import { Input, Select, SelectItem, Textarea } from "@heroui/react";
import { Plus } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useState } from "react";
import {
  LegalProcedureType,
  PartyRole,
  LEGAL_PROCEDURE_TYPE_LABELS,
  PARTY_ROLE_LABELS,
} from "shared/types/legal-procedure";

interface LegalProcedureFormProps {
  onSubmit: (data: {
    type: LegalProcedureType;
    partyRole: PartyRole;
    caseNumber?: string;
    court?: string;
    claimedAmount?: number;
    remarks?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

const TYPE_OPTIONS = Object.entries(LEGAL_PROCEDURE_TYPE_LABELS).map(
  ([key, label]) => ({ key, label })
);

const PARTY_ROLE_OPTIONS = Object.entries(PARTY_ROLE_LABELS).map(
  ([key, label]) => ({ key, label })
);

export const LegalProcedureForm = ({
  onSubmit,
  onCancel,
  isSaving,
}: LegalProcedureFormProps) => {
  const [type, setType] = useState<LegalProcedureType>(
    LegalProcedureType.VREDEGERECHT
  );
  const [partyRole, setPartyRole] = useState<PartyRole>(PartyRole.EISENDE);
  const [caseNumber, setCaseNumber] = useState("");
  const [court, setCourt] = useState("");
  const [claimedAmount, setClaimedAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  const handleSubmit = async () => {
    await onSubmit({
      type,
      partyRole,
      caseNumber: caseNumber || undefined,
      court: court || undefined,
      claimedAmount: claimedAmount ? parseFloat(claimedAmount) : undefined,
      remarks: remarks || undefined,
    });
  };

  return (
    <div className="border rounded-lg p-4 bg-neutral-50">
      <h4 className="text-sm font-medium mb-3">Nieuwe procedure aanmaken</h4>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Type procedure"
            selectedKeys={[type]}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as LegalProcedureType;
              if (key) setType(key);
            }}
            size="sm"
          >
            {TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.key}>{option.label}</SelectItem>
            ))}
          </Select>

          <Select
            label="Onze rol"
            selectedKeys={[partyRole]}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as PartyRole;
              if (key) setPartyRole(key);
            }}
            size="sm"
          >
            {PARTY_ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.key}>{option.label}</SelectItem>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Rolnummer / Zaaknummer"
            placeholder="Optioneel..."
            value={caseNumber}
            onValueChange={setCaseNumber}
            size="sm"
          />
          <Input
            label="Rechtbank"
            placeholder="Naam / locatie..."
            value={court}
            onValueChange={setCourt}
            size="sm"
          />
        </div>

        <Input
          label="Gevorderd bedrag"
          type="number"
          placeholder="0,00"
          value={claimedAmount}
          onValueChange={setClaimedAmount}
          size="sm"
          startContent={
            <span className="text-xs text-neutral-secondary-mid">&#8364;</span>
          }
        />

        <Textarea
          label="Opmerkingen"
          placeholder="Optionele opmerkingen..."
          value={remarks}
          onValueChange={setRemarks}
          size="sm"
          minRows={2}
        />

        <div className="flex justify-end gap-2">
          <ButtonNew
            variant="secondaryNeutral"
            label="Annuleren"
            onClick={onCancel}
          />
          <ButtonNew
            variant="primaryMoss"
            icon={<Plus size={12} />}
            label={isSaving ? "Aanmaken..." : "Aanmaken"}
            onClick={handleSubmit}
            isDisabled={isSaving}
          />
        </div>
      </div>
    </div>
  );
};
